import { type ReactNode } from 'react';

/**
 * A deliberately small markdown renderer for report content.
 *
 * Why not a library, and why not `dangerouslySetInnerHTML`:
 *  - The content is written either by our own rule-based writer (a known, fixed
 *    shape: `### Heading`, paragraphs, `- ` bullets) or by Vertex AI Gemini,
 *    which is free-form. Untrusted-ish text plus `innerHTML` is an XSS sink, and
 *    a TCS admin can additionally paste arbitrary edited copy before release.
 *  - Building React elements instead means there is no HTML parse step at all,
 *    so no markup in the source can ever become live DOM.
 *  - Osprey ships no markdown dependency today; this covers the emitted subset
 *    without adding one.
 *
 * Supported: ATX headings (#..######), unordered lists (-, *), ordered lists,
 * blockquotes, fenced code blocks, horizontal rules, paragraphs, and the inline
 * spans `**bold**`, `*italic*`/`_italic_` and `` `code` ``. Anything else renders
 * as its literal text, which is the safe failure direction.
 */

import { parseBlocks, parseInline } from './markdownParser';

const renderInline = (line: string, keyPrefix: string): ReactNode[] =>
  parseInline(line).map((run, idx) => {
    const key = `${keyPrefix}-${idx}`;
    if (run.code) {
      return (
        <code key={key} className="rounded bg-muted px-1 py-0.5 font-mono text-[12px] text-foreground">
          {run.text}
        </code>
      );
    }
    if (run.bold) {
      return (
        <strong key={key} className="font-semibold text-foreground">
          {run.text}
        </strong>
      );
    }
    if (run.italic) {
      return (
        <em key={key} className="italic">
          {run.text}
        </em>
      );
    }
    return <span key={key}>{run.text}</span>;
  });

const HEADING_CLASSES: Record<number, string> = {
  1: 'text-[20px] font-bold text-foreground mt-6 mb-2 first:mt-0',
  2: 'text-[17px] font-bold text-foreground mt-6 mb-2 first:mt-0',
  3: 'text-[15px] font-semibold text-foreground mt-6 mb-2 first:mt-0',
  4: 'text-[14px] font-semibold text-foreground mt-4 mb-1 first:mt-0',
  5: 'text-[13px] font-semibold text-foreground mt-4 mb-1 first:mt-0',
  6: 'text-[13px] font-semibold text-muted-foreground mt-4 mb-1 first:mt-0',
};

/** Renders report markdown as React elements. No HTML is ever parsed. */
export function ReportMarkdown({ content, className = '' }: { content: string; className?: string }) {
  const blocks = parseBlocks(content);

  return (
    <div className={`text-[14px] leading-relaxed text-muted-foreground ${className}`}>
      {blocks.map((block, idx) => {
        const key = `b${idx}`;
        switch (block.kind) {
          case 'heading': {
            const Tag = `h${Math.min(block.level + 1, 6)}` as 'h2';
            return (
              <Tag key={key} className={HEADING_CLASSES[block.level] ?? HEADING_CLASSES[3]}>
                {renderInline(block.text, key)}
              </Tag>
            );
          }
          case 'ul':
            return (
              <ul key={key} className="list-disc pl-5 my-2 space-y-1">
                {block.items.map((item, j) => (
                  <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={key} className="list-decimal pl-5 my-2 space-y-1">
                {block.items.map((item, j) => (
                  <li key={`${key}-${j}`}>{renderInline(item, `${key}-${j}`)}</li>
                ))}
              </ol>
            );
          case 'quote':
            return (
              <blockquote key={key} className="border-l-2 border-border pl-4 my-3 italic">
                {block.lines.map((l, j) => (
                  <p key={`${key}-${j}`}>{renderInline(l, `${key}-${j}`)}</p>
                ))}
              </blockquote>
            );
          case 'code':
            return (
              <pre
                key={key}
                className="my-3 overflow-x-auto rounded-[10px] bg-muted p-3 font-mono text-[12px] text-foreground"
              >
                {block.lines.join('\n')}
              </pre>
            );
          case 'hr':
            return <hr key={key} className="my-5 border-border" />;
          case 'paragraph':
          default:
            return (
              <p key={key} className="my-2">
                {block.lines.map((l, j) => (
                  <span key={`${key}-${j}`}>
                    {renderInline(l, `${key}-${j}`)}
                    {j < block.lines.length - 1 ? ' ' : null}
                  </span>
                ))}
              </p>
            );
        }
      })}
    </div>
  );
}
